import React, {forwardRef, useImperativeHandle, useState} from "react";
import {useQueryClient} from "react-query";
import __ from "lodash";
import { InputLabel, Select, MenuItem, FormControl, TextField, Tooltip, Grid, Icon, Button } from "@material-ui/core";
import { useContracts } from "../services/contracts";
import moment from "moment";
import DocumentViewer from "../modules/Documents/DocumentViewer";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const ContractSelector = forwardRef((props, ref) => {
  const {id, label, readOnly, disabled, renderValue, style, fullWidth, displayOnly, filter} = props;
  const classNames = [props.className];
  if (props.readOnly) {
    classNames.push('readOnly');
  }
  let value = props.value;
  if (value && typeof value === "object") {
    if (!__.isEmpty(value))
      value = value.code;
    else
      value = "";
  }
  const className = classNames.join(" ");
  const isDataGrid = props.isdatagrid || false;
  const [oldValue, setOldValue] = useState(value);
  const [controlValue, setControlValue] = useState(`${value}`);
  const [selectedContract, setSelectedContract] = useState(undefined);

  if (value !== oldValue) {
    setOldValue(value);
    setControlValue(`${value}`);
  }

  const [viewDocuments, setViewDocuments] = useState ({
    open: false,
    documentsToView: null,
  });

  const queryClient = useQueryClient();
  const extraKeys = props.contractType?['null', props.contractType, props.companyUuid]:['null', 'null', props.companyUuid]

  const contracts = useContracts({queryClient, extraKeys, useCache: true});

  let filteredContracts = [];
  if (contracts && Array.isArray(contracts.data)) {
    filteredContracts = contracts.data;
    if (typeof props.filter === "function") {
      filteredContracts = props.filter(filteredContracts);
    }
    if (typeof props.sort === "function") {
      filteredContracts = props.sort(filteredContracts)
    }
  }

  const viewContractDocument = (contractDocument, contractName) => {
    const documentsToView = [];
    if (contractDocument && contractDocument !== "") {
      const fileNameParts = contractDocument.split(".");
      const filePathParts = contractDocument.split("/");
      const documentType = fileNameParts && Array.isArray(fileNameParts) && fileNameParts.length > 0 ? fileNameParts[fileNameParts.length - 1] : "";
      const fileName = filePathParts && Array.isArray(filePathParts) && filePathParts.length > 0 ? filePathParts[filePathParts.length - 1] : "";
      const documentDetails = {
        documentDescription: contractName,
        documentType: documentType,
        fileName: fileName,
        fileType: documentType,
        uri: contractDocument
      }
      documentsToView.push(documentDetails);
    }
    if (documentsToView.length > 0) {
      setViewDocuments({...setViewDocuments, open: true, documentsToView: documentsToView});
    }
  }

  const downloadContract = (contractFile) => {
    window.open(contractFile, "_blank");
  }

  const onChangeHandler = (event) => {
    if (props.onChange && props.returnType !== "object" && props.returnType !== "objectOnly") {
      props.onChange(event.target.value);
    }
    setControlValue(event.target.value);
    let selectedRecord = __.find(contracts.data, {contractUuid: event.target.value});
    if (selectedRecord) {
      setSelectedContract(selectedRecord);
    } else {
      selectedRecord = __.find(contracts.data, {contractReference: event.target.value});
      if (selectedRecord) {
        setSelectedContract(selectedRecord);
      }
    }
    if (props.onChange && props.returnType === "object") {
      props.onChange(event.target.value, selectedRecord);
    }
    if (props.onChange && props.returnType === "objectOnly") {
      props.onChange(selectedRecord);
    }
  }

  useImperativeHandle(ref, () => {
    let value = {};
    if (isDataGrid)
      value = selectedContract === undefined ? {} : selectedContract;
    else
      value = controlValue === undefined ? {} : controlValue;

    return {
      // the final value to send to the grid, on completion of editing
      getValue() {
        return value;
      },
      // Gets called once before editing starts, to give editor a chance to
      // cancel the editing before it even starts.
      isCancelBeforeStart() {
        return false;
      },

      // Gets called once when editing is finished (eg if enter is pressed).
      // If you return true, then the result of the edit will be ignored.
      isCancelAfterEnd() {
        return false;
      },

      afterGuiAttached () {
        //inputRef.current.focus();
      },
    }
  });


  let displayValue = "";
  let contractEntry = {};
  if (displayOnly && contracts && filteredContracts) {
    let contractUuid = `${value}`;
    displayValue = contractUuid;
    contractEntry = __.find(filteredContracts, {contractUuid: contractUuid});
    if (contractEntry) {
      displayValue = contractEntry.contractReference;
    }
  }

  let labelProps = {...props.inputLabelProps};
  if (props.label && (!__.isEmpty(controlValue) || !__.has(props?.inputLabelProps, ["shrink"]))) {
    labelProps = {shrink: !__.isEmpty(controlValue)}
  }

  return (
    <>
    {displayOnly
      ? label ?
        <FormControl style={props.containerStyle} fullWidth>
          <InputLabel id={`${id}-label`} {...labelProps}>{label}</InputLabel>
          <div style={{marginTop: 20, ...props.style||{}}}>{displayValue}</div>
        </FormControl>
      :
      contractEntry?.contractUuid?
        <Tooltip interactive={true} arrow={true} placement="top" title={
          <div style={{width: 250, fontSize: 12}}>
            <Grid container justifyContent="center" spacing={2}>
              <Grid item xs={12}><div style={{fontWeight: "bold", textAlign: "center"}}>{contractEntry.contractName}</div></Grid>
              <Grid item xs={4}><div style={{fontWeight: "500"}}>Reference:</div></Grid>
              <Grid item xs={8}><div>{contractEntry.contractReference}</div></Grid>
              <Grid item xs={4}><div style={{fontWeight: "500"}}>Revision:</div></Grid>
              <Grid item xs={8}><div>{contractEntry.revision}</div></Grid>
              <Grid item xs={4}><div style={{fontWeight: "500"}}>Period:</div></Grid>
              <Grid item xs={8}><div>{moment.utc(contractEntry.contractStartDate).format("DD MMM YYYY")} - {moment.utc(contractEntry.contractEndDate).format("DD MMM YYYY")}</div></Grid>
            </Grid>
            {contractEntry.contractDocument?
            <Grid container justifyContent="center" spacing={2}>
              <Grid item xs={6}>
                <Button size="small" variant="contained" startIcon={(<Icon>description_outlined</Icon>)} onClick={() => viewContractDocument(contractEntry.contractDocument, contractEntry.contractName)} color="primary" fullWidth>
                  View
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button size="small" variant="contained" startIcon={(<Icon>get_app</Icon>)} onClick={() => downloadContract(contractEntry.contractDocument)} color="primary" fullWidth>
                  Download
                </Button>
              </Grid>
            </Grid>
            :<></>}
          </div>
        }>
          <div style={{...props.style||{}}}>{displayValue}</div>
        </Tooltip>
        : <div style={{...props.style||{}}}></div>
      :
        <FormControl style={props.containerStyle} fullWidth>
          {label
            ?
              <InputLabel id={`${id}-label`} {...labelProps}>{label}</InputLabel>
            :
              <></>
          }
          <Select
            id={id}
            labelId={`${id}-label`}
            className={className}
            readOnly={readOnly}
            disabled={disabled}
            fullWidth={fullWidth}
            onChange={onChangeHandler}
            value={controlValue}
            style={style}
            MenuProps={{
              getContentAnchorEl: null,
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left"
              },
              PaperProps: {
                style: {
                  maxHeight: ITEM_HEIGHT * 9.5 + ITEM_PADDING_TOP,
                }}
            }}
            renderValue={(contractUuid) => {
              debugger;
              let value = contractUuid;
              if (typeof renderValue === "function"){
                value = renderValue(contractUuid, filteredContracts);
              } else {
                const contract = __.find(filteredContracts, {contractUuid: contractUuid});
                if (contract) {
                  value = contract.contractReference;
                } else {
                  const contract = __.find(filteredContracts, {contractReference: contractUuid});
                  if (contract)
                    value = contract.contractReference;
                }
              }
              return value;
            }}
            {...props.inputProps}
          >
            {(contracts && Array.isArray(filteredContracts)) ?
              filteredContracts.map((contractEntry) => {
                return (
                  <MenuItem key={contractEntry.contractUuid} value={contractEntry.contractUuid}>{contractEntry.contractReference} - {contractEntry.contractName}</MenuItem>);
              }) :
              null
            }
          </Select>
        </FormControl>
    }
    {viewDocuments.open
      ?
        <DocumentViewer
          dialogStates={
            {showDialog: viewDocuments.open,
              title: `Contract Viewer`,
              record: viewDocuments.documentsToView[0]
            }
          }
          close={()=>{setViewDocuments({...viewDocuments, open: false})}}
          documents={viewDocuments.documentsToView}
        />
      :
        <></>
    }
    </>
  );
})

export default (ContractSelector);
